<script setup lang="ts">
import { EnvironmentStatus } from '@onereach/billing-shared';
import { OrSelectV3, SelectItem } from '@onereach/ui-components';
import { useDebounceFn, tryOnScopeDispose } from '@vueuse/core';
import { ref, watch } from 'vue';

import { useEnvironmentsStore } from '@/stores';
import type { RemoteEnvironmentTarget } from '@/types/remote-environment-target.types';

const props = withDefaults(defineProps<{
  modelValue: string[] | string | undefined;
  multiple?: boolean;
  placeholder?: string | undefined;
  disabled?: boolean;
  readonly?: boolean;
  required?: boolean;
  minLength?: number;
  limit?: number;
  debounceMs?: number;
  label?: string | undefined;
  error?: string | undefined;
  customerIds?: string[];
  status?: EnvironmentStatus;
  client?: string;
  environment?: string;
  allowedTargets?: RemoteEnvironmentTarget[];
}>(), {
  multiple: false,
  debounceMs: 300,
  minLength: 2,
  limit: 20,
  placeholder: undefined,
  label: undefined,
  error: undefined,
  disabled: false,
  readonly: false,
  required: false,
  customerIds: undefined,
  status: EnvironmentStatus.ACTIVE,
  client: undefined,
  environment: undefined,
  allowedTargets: undefined,
});

const emit = defineEmits<{
  (e: 'update:modelValue', v: string[] | string | undefined): void;
}>();

const environmentsStore = useEnvironmentsStore();

const options = ref<SelectItem[]>([]);
const searchOptions = ref<SelectItem[]>([]);
const isLoading = ref(false);
const suppressClearOnce = ref(false);
const searchToken = ref(0);

const selected = ref<string[]>([]);

function normalizeToArray(v: string[] | string | undefined): string[] {
  if (Array.isArray(v)) return v.filter(Boolean);
  if (typeof v === 'string' && v) return [v];
  return [];
}

function toOrSelectModel(): string[] | string | undefined {
  return props.multiple ? selected.value : (selected.value[0] ?? undefined);
}

watch(
  () => props.modelValue,
  (v) => {
    selected.value = normalizeToArray(v as unknown as string[] | string | undefined);
  },
  { immediate: true },
);

async function ensureSelectedOptions(ids: string[]) {
  const existing = new Set(options.value.map((o: SelectItem) => o.value));
  const toFetch: string[] = [];
  for (const id of ids) {
    if (existing.has(id)) continue;
    const environment = environmentsStore.entities[id];
    if (environment) {
      options.value.push({
        value: id,
        label: environment.friendlyName || environment.client || id,
      });
    } else {
      toFetch.push(id);
    }
  }
  if (toFetch.length) {
    await Promise.all(toFetch.map(async (id) => {
      const env = await environmentsStore.loadEnvironmentById(id);
      options.value.push({
        value: id,
        label: env?.friendlyName || env?.client || id,
      });
    }));
  }
}

watch(selected, (ids) => { void ensureSelectedOptions(ids); }, { immediate: true });

async function doSearch(token: number, term: string) {
  if (token !== searchToken.value) return;
  try {
    isLoading.value = true;
    const filters = {
      customerIds: props.customerIds,
      status: props.status,
      client: props.client,
      environment: props.environment,
      allowedTargets: props.allowedTargets,
    };
    const res = await environmentsStore.searchForSelect(term, props.limit, filters);
    if (token !== searchToken.value) return;
    searchOptions.value = res.map(r => ({
      value: r.id,
      label: r.friendlyName || r.client || r.id,
    }));
  } finally {
    if (token === searchToken.value) isLoading.value = false;
  }
}

const onSearchDebounced = useDebounceFn(
  (term: string, token: number) => { void doSearch(token, term); },
  props.debounceMs,
);

function cancelDebounced() {
  (onSearchDebounced as unknown as { cancel?: () => void }).cancel?.();
}

function onSearch(term: string) {
  const t = (term || '').trim();
  if (t.length < props.minLength) {
    cancelDebounced();
    isLoading.value = false;
    searchToken.value++;
    if (suppressClearOnce.value) {
      suppressClearOnce.value = false;
      return;
    }
    searchOptions.value = [];
    return;
  }

  const token = ++searchToken.value;
  onSearchDebounced(t, token);
}

function update(val: unknown) {
  cancelDebounced();
  isLoading.value = false;

  const nextArr = normalizeToArray(val as string[] | string | undefined);
  const isCleared = nextArr.length === 0;

  if (isCleared) {
    options.value = [];
    searchOptions.value = [];
    suppressClearOnce.value = false;
    searchToken.value++;
  } else {
    suppressClearOnce.value = true;
    searchToken.value++;
  }

  selected.value = nextArr;
  const out = props.multiple ? nextArr : (nextArr[0] ?? undefined);
  emit('update:modelValue', out);
}

tryOnScopeDispose(() => {
  cancelDebounced();
});
</script>

<template>
  <OrSelectV3
    :model-value="toOrSelectModel()"
    :multiple="multiple"
    :options="options"
    :search-options="searchOptions"
    external-control
    enable-search
    enable-clear
    :loading="isLoading"
    :label="label"
    overflow="hidden"
    :placeholder="placeholder || $t('widgets.environmentSelect.placeholder')"
    :error="error"
    :disabled="disabled"
    :readonly="readonly"
    :required="required"
    @update:model-value="update"
    @search="onSearch"
  >
    <template #noSearchResultsTemplate="{searchText}">
      <div class="typography-body-2-regular">
        <template v-if="searchText">
          {{ $t('widgets.environmentSelect.noSearchResults') }}
        </template>
        <template v-else>
          {{ $t('widgets.environmentSelect.startTyping') }}
        </template>
      </div>
    </template>
  </OrSelectV3>
</template>
